import { Badge, Button, Popover, Stack, Typography } from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { useState, type ComponentPropsWithoutRef } from "react";
import { z } from "zod";
import { TabsComponent } from "@/components/core/tabs-component";
import { ActivityTable } from "@/components/tables/activity";
import { HandleReplyStep, TaskStatus, useGetToNotify } from "@/models/backend";
import { OutboxTable } from "@/components/tables/outbox";
import { CustomerSelector } from "@/components/core/customer-selector";

export const tabs = ["outbox", "inbox", "sent"] as const;

const filters = z.object({
  tab: z.enum(tabs).default("outbox").catch("outbox"),
  review: z.enum({ ...TaskStatus, ...HandleReplyStep }).optional(),
  eventId: z.uuid().optional(),
});

export const Route = createFileRoute("/_authenticated/_contained/events/")({
  component: RouteComponent,
  validateSearch: filters,
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const { data: toNotify } = useGetToNotify();

  const search = Route.useSearch();

  const tabObjs: (ComponentPropsWithoutRef<typeof TabsComponent>["tabs"][number] & {
    id: (typeof tabs)[number];
  })[] = [
    {
      id: "outbox",
      label: (
        <Stack spacing={1} direction="row">
          Outbox
          {toNotify && toNotify.outbox_due > 0 ? (
            <Badge color="primary" badgeContent={toNotify.outbox_due} />
          ) : null}
        </Stack>
      ),
      component: <OutboxTable />,
      extraProps: { sx: { overflow: "visible", ml: 3 } },
    },
    {
      id: "inbox",
      label: (
        <Stack spacing={1} direction="row">
          Inbox
          {toNotify && toNotify.new_incoming > 0 ? (
            <Badge color="primary" badgeContent={toNotify.new_incoming} />
          ) : null}
        </Stack>
      ),
      component: <ActivityTable type="inbox" />,
      extraProps: { sx: { overflow: "visible", ml: 3 } },
    },
    {
      id: "sent",
      label: "Sent",
      component: <ActivityTable type="sent" />,
      extraProps: { sx: { ml: 3 } },
    },
  ];

  const [openSelector, setOpenSelector] = useState<HTMLButtonElement | null>(null);

  return (
    <Stack spacing={2}>
      <Typography variant="h3">Activity</Typography>

      <Stack sx={{ width: "100%" }} spacing={4}>
        <TabsComponent
          tabs={tabObjs}
          action={
            <Button
              variant="outlined"
              size="small"
              onClick={(e) => setOpenSelector(openSelector ? null : e.currentTarget)}
            >
              Compose new
            </Button>
          }
          selectedTab={tabObjs.findIndex((tabObj) => tabObj.id === search.tab)}
          onSelectedTab={(idx) => {
            navigate({ search: { ...search, tab: tabObjs[idx].id } });
          }}
        />
      </Stack>
      <Popover
        open={openSelector !== null}
        anchorEl={openSelector}
        onClose={() => setOpenSelector(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <CustomerSelector
          value={null}
          onChange={(customer) =>
            customer != null &&
            "id" in customer &&
            navigate({ to: "/customers/$customerId/compose", params: { customerId: customer.id } })
          }
        />
      </Popover>
    </Stack>
  );
}
